import { useEffect, useState } from 'react';
import { User } from 'services/auth/auth.types';
import { useGetUserProjectEventsMutation } from 'services/person/event/event.endpoint';
import { PersonProjectEvent } from 'services/person/event/event.types';
import { useGetUserProfileMutation } from 'services/user/user.endpoint';
import moment from 'moment';
import { MIME_TYPE_LABELS } from 'services/document/document.types';
import { Document } from 'services/document/document.types';
import { useUpdateEventDocumentMutation } from 'services/document/document.endpoint';
import { useSelector } from 'react-redux';
import { getDatalists } from 'features/datalist';

export default function useProjectVM() {
    const [me, setMe] = useState<User>({} as User);
    const [events, setEvents] = useState<Array<PersonProjectEvent>>([]);
    const [selectedEvent, setSelectedEvent] = useState<PersonProjectEvent | null>(null);
    const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
    const [isDocumentModalOpen, setIsDocumentModalOpen] = useState<boolean>(false);
    const [documentModifications, setDocumentModifications] = useState<Document | null>(null);
    const [isUserHasProject, setIsUserHasProject] = useState<boolean>(true);
    const { datalists } = useSelector(getDatalists);

    function formatDate(date: string) {
        return moment(date).format('DD MMMM YYYY');
    }

    const getMimeTypeLabel = (mimeType: string | undefined): string => {
        if (!mimeType) return 'Type inconnu';
        return MIME_TYPE_LABELS[mimeType] || 'Type inconnu';
    };

    const [getMe, { isLoading: isMeLoading, isError: isMeError, isSuccess: isMeSuccess, error: meError, data: meResponse }] =
        useGetUserProfileMutation();

    const [
        getUserProjectEvents,
        {
            isLoading: isUserProjectEventsLoading,
            isError: isUserProjectEventsError,
            isSuccess: isUserProjectEventsSuccess,
            error: UserProjectEventsError,
            data: UserProjectEventsResponse
        }
    ] = useGetUserProjectEventsMutation();

    const [
        updateEventDocument,
        {
            isLoading: isUpdateEventDocumentLoading,
            isError: isUpdateEventDocumentError,
            isSuccess: isUpdateEventDocumentSuccess,
            error: updateEventDocumentError,
            data: updateEventDocumentResponse
        }
    ] = useUpdateEventDocumentMutation();

    useEffect(() => {
        getMe(undefined);
    }, []);

    useEffect(() => {
        if (isMeLoading) return;
        if (isMeError && meError) {
            return;
        }
        if (isMeSuccess && meResponse) {
            setMe(meResponse.data);
        }
    }, [isMeLoading, isMeError, isMeSuccess, meError, meResponse]);

    useEffect(() => {
        if (me?.projects?.length === 0) {
            setIsUserHasProject(false);
            return;
        }
        setIsUserHasProject(true);
        if (me?.projects?.[0]?.id) {
            getUserProjectEvents({
                id: me.projects[0].id
            });
        }
    }, [me, getUserProjectEvents]);

    useEffect(() => {
        setSelectedEvent(events.at(-1) || null);
    }, [events]);

    useEffect(() => {
        if (isUserProjectEventsLoading) return;
        if (isUserProjectEventsError && UserProjectEventsError) {
            return;
        }
        if (isUserProjectEventsSuccess && UserProjectEventsResponse) {
            const today = moment().startOf('day');
            const pastEvents = UserProjectEventsResponse.data
                .filter((event) => moment(event.started_at).startOf('day').isBefore(today))
                .sort((a, b) => moment(a.started_at).valueOf() - moment(b.started_at).valueOf());

            setEvents(pastEvents);
        }
    }, [
        isUserProjectEventsLoading,
        isUserProjectEventsError,
        isUserProjectEventsSuccess,
        UserProjectEventsError,
        UserProjectEventsResponse
    ]);

    useEffect(() => {
        if (isUpdateEventDocumentLoading) return;
        if (isUpdateEventDocumentError && updateEventDocumentError) {
            return;
        }
        if (isUpdateEventDocumentSuccess && updateEventDocumentResponse) {
            return;
        }
    }, [
        isUserProjectEventsLoading,
        isUpdateEventDocumentError,
        isUpdateEventDocumentSuccess,
        updateEventDocumentError,
        updateEventDocumentResponse
    ]);

    const handleUpdateDocument = async () => {
        if (!selectedEvent || !documentModifications) return;

        const response = await updateEventDocument({
            project_id: me.projects[0].id,
            event_id: selectedEvent.id,
            document_id: documentModifications.id,
            label: documentModifications.label,
            observation: documentModifications.observation
        });

        if (response.data) {
            setSelectedEvent(response.data);
            setIsDocumentModalOpen(false);
            setDocumentModifications(null);
        }
    };

    return {
        events,
        me,
        formatDate,
        selectedEvent,
        setSelectedEvent,
        getMimeTypeLabel,
        selectedDocument,
        setSelectedDocument,
        isDocumentModalOpen,
        setIsDocumentModalOpen,
        documentModifications,
        setDocumentModifications,
        handleUpdateDocument,
        isApiRequestLoading: isUserProjectEventsLoading,
        datalists,
        isUserHasProject
    };
}
